import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { debounce } from "lodash";

const createFilterQueryString = (filters) => {
	let query = [];
	Object.keys(filters).forEach((fil) => {
		if (filters[fil]) {
			query.push(`${fil}=${filters[fil]}`);
		}
	});

	return "?" + query.join("&");
};

const normalizar = (value) =>
	value instanceof Array ? value.map((v) => v.value).join(",") : value;

const useFilters = (initialFilters) => {
	const [filters, setFilters] = useState(initialFilters);
	const [filtersQuery, setFiltersQuery] = useState(
		createFilterQueryString(initialFilters)
	);
	// Marca si el cambio pendiente vino por la vía con debounce. Se apaga sola
	// después de atenderlo, para que un `setFilters` directo siga siendo inmediato.
	const esperarDebounce = useRef(false);

	// El debounce va sobre el query —lo que dispara la petición— y no sobre el
	// cambio de filtro. Era un único debounce compartido por todos los campos y
	// lodash se queda con los últimos argumentos, así que cambiar dos filtros en
	// menos de 500ms descartaba el primero junto con su valor: el usuario escribía
	// en "Buscar", tocaba un select y su búsqueda nunca llegaba al estado.
	const actualizarQuery = useMemo(
		() =>
			debounce(
				(filtros) => setFiltersQuery(createFilterQueryString(filtros)),
				500
			),
		[]
	);

	const cambiarFiltro = (key, value) =>
		setFilters((state) => ({ ...state, [key]: normalizar(value) }));

	const handleFilterChange = (key, value) => {
		esperarDebounce.current = true;
		cambiarFiltro(key, value);
	};

	const handleFilterChangeWithoutDebounce = (key, value) => {
		esperarDebounce.current = false;
		cambiarFiltro(key, value);
	};

	useEffect(() => {
		if (!esperarDebounce.current) {
			// Un cambio inmediato cancela el debounce pendiente: el query que se
			// arma aquí ya incluye los filtros que estaban esperando.
			actualizarQuery.cancel();
			setFiltersQuery(createFilterQueryString(filters));
			return;
		}

		esperarDebounce.current = false;
		actualizarQuery(filters);
	}, [filters, actualizarQuery]);

	useEffect(() => () => actualizarQuery.cancel(), [actualizarQuery]);

	return { filters, handleFilterChange, handleFilterChangeWithoutDebounce, filtersQuery, setFilters };
};

useFilters.propTypes = {
	initialFilters: PropTypes.object,
};

export default useFilters;
